import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Col from 'react-bootstrap/lib/Col';

import { computeUserRank, ranks } from 'helpers/UserRank';
import { isVerificationEligible } from 'helpers/VerificationEligibility';

import ProgressBar from './components/ProgressBar';
import UserRankSteps from './components/UserRankSteps';
import VerificationStrip from './components/VerificationStrip';
import VerificationBanner from './components/VerificationBanner';
import VerifiedInfoModal from './components/VerifiedInfoModal';

import styles from './Onboarding.module.scss';

const Onboarding = ({ user }) => {
  const location = useLocation();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const currentRank = computeUserRank(user);
  const eligibleForVerification = isVerificationEligible(user);
  const isHighRank = currentRank === ranks.HIGH_RANK;

  // Check if we're on a Portfolio page (matches /:username/:option? pattern)
  const isPortfolioPage =
    location.pathname !== '/' &&
    /^\/[A-Za-z0-9_]{1,20}(\/.*)?$/.test(location.pathname) &&
    !location.pathname.startsWith('/jobs') &&
    !location.pathname.startsWith('/projects') &&
    !location.pathname.startsWith('/browse') &&
    !location.pathname.startsWith('/onboarding') &&
    !location.pathname.startsWith('/notifications') &&
    !location.pathname.startsWith('/manage') &&
    !location.pathname.startsWith('/messages') &&
    !location.pathname.startsWith('/report') &&
    !location.pathname.startsWith('/saved-jobs') &&
    !location.pathname.startsWith('/shortlist') &&
    !location.pathname.startsWith('/settings') &&
    !location.pathname.startsWith('/payments') &&
    !location.pathname.startsWith('/earnings') &&
    !location.pathname.startsWith('/post') &&
    !location.pathname.startsWith('/pricing');

  const bannerClasses = [
    styles.onboardingBanner,
    isPortfolioPage ? styles.onboardingBannerPortfolio : '',
  ]
    .filter(Boolean)
    .join(' ');

  // A profile-score banner is shown for Low/Medium rank. High rank only shows
  // when the user is eligible for verification (score is complete otherwise).
  const showScoreBanner =
    currentRank === ranks.LOW_RANK || currentRank === ranks.MEDIUM_RANK;
  const showHighVerificationOnly = isHighRank && eligibleForVerification;

  if (currentRank === ranks.INVALID_RANK) {
    return <span />;
  }

  if (!showScoreBanner && !showHighVerificationOnly) {
    return <span />;
  }

  return (
    <>
      <Col id="static-banner" className={bannerClasses}>
        <div className={styles.onboardingBannerContainer}>
          <div className={styles.onboardingBannerContainerInner}>
            {showScoreBanner && (
              <>
                <div className={styles.rankBannerContainer}>
                  <ProgressBar user={user} currentRank={currentRank} />
                  <UserRankSteps user={user} currentRank={currentRank} />
                </div>

                {eligibleForVerification && (
                  <VerificationStrip
                    onLearnMore={() => setShowInfoModal(true)}
                  />
                )}
              </>
            )}

            {showHighVerificationOnly && (
              <VerificationBanner onLearnMore={() => setShowInfoModal(true)} />
            )}
          </div>
        </div>
      </Col>

      <VerifiedInfoModal
        show={showInfoModal}
        onClose={() => setShowInfoModal(false)}
      />
    </>
  );
};

Onboarding.propTypes = {
  user: PropTypes.object,
};

export default connect(
  (state) => ({
    user: state.auth.user,
  }),
  {
    //
  },
  null,
  { forwardRef: true }
)(Onboarding);
